import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IrsRatingConfig } from '../../models/IrsRatingRow.model';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { Popup } from '../../../../../core/shared/components/popup/popup';
import { ToastService } from '../../../../../core/shared/services/toast.service';
import { CraditService } from '../../services/cradit.service';
import { RichTextEditor } from '../rich-text-editor/rich-text-editor';
import { RichTextPipe } from '../../utils/rich-text.pipe';
import { richTextForSave } from '../../utils/rich-text.util';

@Component({
  selector: 'app-irs-rating-section',
  imports: [CommonModule, FormsModule, Popup, TranslatePipe, RichTextEditor, RichTextPipe],
  templateUrl: './irs-rating-section.html',
  styleUrl: './irs-rating-section.css',
})
export class IrsRatingSection implements OnInit, OnDestroy {
  @Input() config!: IrsRatingConfig;
  @Input() editable = false;
  @Input() sectionId = '';

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  onboardingId!: number;
  crdtPrpslSeq: number | null = null;
  crdtPrpslBlkSeq: number | null = null; // ✅ block tracking, taake update ho duplicate na bane

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteIndex: number | null = null;

  private saveSub?: Subscription;
  private seqSub?: Subscription;

  ngOnInit(): void {
    this.onboardingId = Number(this.route.snapshot.paramMap.get('id'));

    // ✅ shared crdtPrpslSeq sync — jab bhi seq update ho, irs rating reload karo
    this.seqSub = this.creditService.crdtPrpslSeq$.subscribe((seq) => {
      this.crdtPrpslSeq = seq;
      this.loadIrsRating();
    });

    this.saveSub = this.creditService.saveSectionTrigger$.subscribe((sectionId) => {
      if (sectionId === this.sectionId) {
        this.saveIrsRating();
      }
    });
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.seqSub?.unsubscribe();
  }

  private isBlank(value: unknown): boolean {
    return !String(value ?? '').trim();
  }

  // The whole section lives in one block, so it only counts as empty when
  // introduction, every row, the final score and conclusion are all blank.
  private isConfigEmpty(): boolean {
    const { introduction, rows, finalScore, conclusion } = this.config ?? ({} as IrsRatingConfig);

    const rowsEmpty = (rows ?? []).every(row =>
      [row.category, row.weight, row.currentScore, row.currentRating, row.previousScore, row.previousRating]
        .every(value => this.isBlank(value))
    );

    const finalScoreEmpty = [
      finalScore?.currentScore,
      finalScore?.currentRating,
      finalScore?.previousScore,
      finalScore?.previousRating,
    ].every(value => this.isBlank(value));

    return this.isBlank(introduction) && rowsEmpty && finalScoreEmpty && this.isBlank(conclusion);
  }

  saveIrsRating(): void {
    const isBlockDeleted = this.isConfigEmpty();

    if (isBlockDeleted && !this.crdtPrpslBlkSeq) {
      // Nothing saved yet and nothing to save.
      this.creditService.reportSectionSaveResult({
        sectionId: this.sectionId,
        status: 'saved',
      });
      return;
    }

    const payload = {

      crprtOnbrdngSeq: this.onboardingId,

      crdtPrpslSeq: this.crdtPrpslSeq, // ✅ shared seq

      blockList: [
        {
          crdtPrpslBlkSeq: this.crdtPrpslBlkSeq, // ✅ existing block update ke liye

          refGrp: 28,

          refValSeq: 2526,

          blkTyp: 3,

          delFlg: isBlockDeleted ? 1 : 0,

          sortOrdr: 1,

          txtData: '',

          jsonData: [
            {
              introduction: richTextForSave(this.config.introduction),

              rows: this.config.rows,

              finalScore: this.config.finalScore,

              conclusion: richTextForSave(this.config.conclusion)
            }
          ]
        }
      ]
    };

    this.creditService
      .saveSection(payload)
      .subscribe({
        next: (res: any) => {
          // ✅ shared service mein propagate karo
          this.creditService.setCrdtPrpslSeq(res?.crdtPrpslSeq);
          this.loadIrsRating();
          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'saved',
          });

          this.toast.success('CREDIT_SECTION_IRS.SAVED');
        },
        error: err => {
          console.error(err);
          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'api-error',
            message: err?.error?.message ?? this.translate.instant('CREDIT_SECTION_IRS.SAVE_FAILED'),
          });
        }
      });
  }

  loadIrsRating(): void {

    // ✅ jab tak shared crdtPrpslSeq na mile, kuch load nahi karna
    if (!this.crdtPrpslSeq) {
      return;
    }

    // ✅ getSections ki jagah getCrdtBlck use karo
    this.creditService
      .getCrdtBlck(this.crdtPrpslSeq, [2526])
      .subscribe({
        next: (res: any[]) => {

          // Ignore soft-deleted blocks so a deleted record is never reused.
          const activeBlock = (res ?? []).find(row => Number(row?.delFlg ?? 0) !== 1);

          if (!activeBlock) {
            this.crdtPrpslBlkSeq = null;
            return;
          }

          this.crdtPrpslBlkSeq = activeBlock.crdtPrpslBlkSeq ?? null; // ✅ track block seq

          const data = activeBlock.jsonData?.[0];

          if (!data) {
            return;
          }

          this.config = {

            introduction: data.introduction || '',

            rows: data.rows || [],

            finalScore: data.finalScore || {
              currentScore: '',
              currentRating: '',
              previousScore: '',
              previousRating: ''
            },

            conclusion: data.conclusion || ''
          };

        },
        error: err => {
          console.error(err);
        }
      });
  }

  addRow(): void {
    this.config.rows.push({
      category: '',
      weight: '',
      currentScore: '',
      currentRating: '',
      previousScore: '',
      previousRating: '',
    });
  }

  requestRemoveRow(index: number): void {
    this.pendingDeleteIndex = index;
    this.deleteMessage = 'CREDIT_SECTION.DELETE_ROW_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmRemoveRow(): void {
    if (this.pendingDeleteIndex === null) return;

    this.removeRow(this.pendingDeleteIndex);
    this.pendingDeleteIndex = null;
    this.toast.success('CREDIT_SECTION.ROW_DELETED');
  }

  private removeRow(index: number): void {
    this.config.rows.splice(index, 1);
  }
}